import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getCategories, createCategory, updateCategory, deleteCategory, Category } from '../api/products.api';
import { createEventWithImage } from '../api/events.api';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteCategoryModal from '../components/DeleteCategoryModal';

const SERVICE_TABS = [
  {
    id: 'hunting-program',
    title: 'Hunting Program',
    icon: 'bi-bullseye',
    description: 'দেশব্যাপী প্রতিভা অন্বেষণ',
    serviceSlug: 'hunting-program'
  },
  {
    id: 'dealership-program',
    title: 'Dealership Program',
    icon: 'bi-handshake',
    description: 'দেশব্যাপী তরুণ উদ্যোক্তা তৈরীর পদক্ষেপ।',
    serviceSlug: 'dealership-program'
  }
];

const AdminProgramsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'hunting-program' | 'dealership-program'>('hunting-program');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; categoryId: string | null; categoryName: string; productCount?: number }>({
    show: false,
    categoryId: null,
    categoryName: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    display_order: 0,
    is_active: true,
    service_slug: '',
    parent_category_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Event creation state
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    category_id: '',
    status: '',
    registrationOpen: false,
  });
  const [eventImageFile, setEventImageFile] = useState<File | null>(null);
  const [eventImagePreview, setEventImagePreview] = useState<string | null>(null);
  const [eventErrors, setEventErrors] = useState<Record<string, string>>({});
  const [eventSaving, setEventSaving] = useState(false);
  const [eventSuccess, setEventSuccess] = useState(false);

  const activeServiceSlug = SERVICE_TABS.find(tab => tab.id === activeTab)?.serviceSlug || 'hunting-program';

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCategories();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Filter categories by active service slug
  const filteredCategories = useMemo(() => {
    let filtered = categories.filter(cat => cat.service_slug === activeServiceSlug);
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cat =>
        cat.name.toLowerCase().includes(query) ||
        cat.slug.toLowerCase().includes(query) ||
        (cat.description && cat.description.toLowerCase().includes(query))
      );
    }
    
    // Sort by display_order
    return filtered.sort((a, b) => a.display_order - b.display_order);
  }, [categories, activeServiceSlug, searchQuery]);

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      display_order: 0,
      is_active: true,
      service_slug: activeServiceSlug, // Pre-filled!
      parent_category_id: '',
    });
    setErrors({});
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (category: Category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      display_order: category.display_order,
      is_active: category.is_active,
      service_slug: category.service_slug || activeServiceSlug,
      parent_category_id: category.parent_category_id || '',
    });
    setErrors({});
    setEditingCategory(category);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      display_order: 0,
      is_active: true,
      service_slug: activeServiceSlug,
      parent_category_id: '',
    });
    setErrors({});
  };

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
    }));
    if (errors.name) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required.';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      // Always set service_slug to active service
      payload.service_slug = activeServiceSlug;

      // Only include parent_category_id if provided
      if (formData.parent_category_id) {
        payload.parent_category_id = formData.parent_category_id;
      } else {
        payload.parent_category_id = null;
      }

      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
      } else {
        await createCategory(payload);
      }
      handleCloseModals();
      fetchCategories();
    } catch (err: any) {
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else {
        setErrors({ general: err.response?.data?.message || 'Failed to save category. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.categoryId) return;

    try {
      await deleteCategory(deleteModal.categoryId);
      setDeleteModal({ show: false, categoryId: null, categoryName: '' });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  // Event creation functions
  const handleOpenCreateEventModal = () => {
    const now = new Date();
    const dateString = now.toISOString().split('T')[0];
    setEventFormData({
      name: '',
      description: '',
      startDate: dateString,
      endDate: '',
      location: '',
      category_id: '',
      status: '',
      registrationOpen: false,
    });
    setEventImageFile(null);
    setEventImagePreview(null);
    setEventErrors({});
    setShowCreateEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowCreateEventModal(false);
    setEventFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      location: '',
      category_id: '',
      status: '',
      registrationOpen: false,
    });
    setEventImageFile(null);
    setEventImagePreview(null);
    setEventErrors({});
  };

  const handleEventChange = (field: string, value: string | boolean) => {
    setEventFormData((prev) => ({ ...prev, [field]: value }));
    if (eventErrors[field]) {
      setEventErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEventFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setEventErrors({ ...eventErrors, image: 'File size exceeds 5MB limit.' });
        e.target.value = '';
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setEventErrors({ ...eventErrors, image: 'Invalid file type. Please upload JPEG, PNG, GIF, or WEBP image.' });
        e.target.value = '';
        return;
      }
      setEventImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setEventErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });
    }
  };

  const validateEventForm = () => {
    const newErrors: Record<string, string> = {};

    if (!eventFormData.category_id) {
      newErrors.category_id = 'Category selection is required.';
    }

    if (!eventFormData.name.trim()) {
      newErrors.name = 'Event name is required.';
    } else if (eventFormData.name.trim().length < 3) {
      newErrors.name = 'Event name must be at least 3 characters.';
    } else if (eventFormData.name.trim().length > 200) {
      newErrors.name = 'Event name must not exceed 200 characters.';
    }

    if (!eventFormData.startDate) {
      newErrors.startDate = 'Start date is required.';
    }

    if (eventFormData.endDate) {
      const startDate = new Date(eventFormData.startDate);
      const endDate = new Date(eventFormData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = 'End date must be after start date.';
      }
    }

    if (!eventImageFile) {
      newErrors.image = 'Event image is required.';
    }

    setEventErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEventErrors({});

    if (!validateEventForm()) {
      return;
    }

    setEventSaving(true);
    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', eventFormData.name.trim());
      if (eventFormData.description) {
        formDataToSend.append('description', eventFormData.description.trim());
      }
      
      const startDate = new Date(eventFormData.startDate);
      formDataToSend.append('start_date', startDate.toISOString());
      
      if (eventFormData.endDate) {
        const endDate = new Date(eventFormData.endDate);
        formDataToSend.append('end_date', endDate.toISOString());
      }
      
      if (eventFormData.location) {
        formDataToSend.append('location', eventFormData.location.trim());
      }
      
      // Add category association - include category name in event name for filtering
      const selectedCategory = filteredCategories.find(cat => cat.id === eventFormData.category_id);
      if (selectedCategory) {
        // Include category name in event name for filtering on EventsPage
        const eventNameWithCategory = `${eventFormData.name.trim()} - ${selectedCategory.name}`;
        formDataToSend.set('name', eventNameWithCategory);
        // Also add category as separate field if backend supports it
        formDataToSend.append('category', selectedCategory.name);
      }
      
      formDataToSend.append('status', eventFormData.status);
      formDataToSend.append('registration_open', eventFormData.registrationOpen.toString());
      
      if (eventImageFile) {
        formDataToSend.append('banner', eventImageFile);
      }

      await createEventWithImage(formDataToSend);
      
      handleCloseEventModal();
      setEventSuccess(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setEventSuccess(false);
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          setEventErrors(errorData.errors);
        } else {
          setEventErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else {
        setEventErrors({ general: err.response?.data?.message || 'Failed to create event. Please try again.' });
      }
    } finally {
      setEventSaving(false);
    }
  };

  const activeTabData = SERVICE_TABS.find(tab => tab.id === activeTab);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>

      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">PROGRAMS MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Manage categories for Hunting Program and Dealership Program</p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/dashboard/admin/events" className="btn btn-outline-primary">
            <i className="bi bi-calendar-event me-2"></i>
            View All Events
          </Link>
          <button className="btn btn-arena" onClick={handleOpenCreateModal}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Category
          </button>
        </div>
      </div>

      {/* Success Message */}
      {eventSuccess && (
        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          Event created successfully!
          <button
            type="button"
            className="btn-close"
            onClick={() => setEventSuccess(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Info Banner */}
      <div className="alert alert-success mb-4">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Event Categories:</strong> Categories created here organize events. When users select a category on the service page, they'll see filtered events on the Events page.
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        {SERVICE_TABS.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id as 'hunting-program' | 'dealership-program');
                setSearchQuery(''); // Reset search when switching tabs
              }}
              type="button"
            >
              <i className={`${tab.icon} me-2`}></i>
              {tab.title}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        <div className="tab-pane fade show active">
          {/* Service Info */}
          {activeTabData && (
            <div className="card card-surface mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div className="service-icon" style={{ fontSize: '2.5rem', color: 'var(--accent-primary)' }}>
                    <i className={`bi ${activeTabData.icon}`} />
                  </div>
                  <div>
                    <h2 className="h5 mb-1">{activeTabData.title}</h2>
                    <p className="text-muted-soft mb-0">{activeTabData.description}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Event Button */}
          <div className="mb-3">
            <button 
              className="btn btn-arena"
              onClick={handleOpenCreateEventModal}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Create Event for {activeTabData?.title}
            </button>
          </div>

          {/* Search */}
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Categories Table */}
          <div className="card card-surface">
            <div className="card-body">
              {filteredCategories.length === 0 ? (
                <div className="alert alert-secondary mb-0">
                  {searchQuery ? 'No categories found matching your search.' : `No categories found for ${activeTabData?.title}.`}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Slug</th>
                        <th>Description</th>
                        <th>Display Order</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((category) => (
                        <tr key={category.id}>
                          <td className="fw-semibold">{category.name}</td>
                          <td>
                            <code className="text-muted-soft">{category.slug}</code>
                          </td>
                          <td>
                            <span className="text-muted-soft small">
                              {category.description || '-'}
                            </span>
                          </td>
                          <td>{category.display_order}</td>
                          <td>
                            <span className={`badge ${category.is_active ? 'bg-success' : 'bg-secondary'} badge-status`}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Link
                                to={`/events?category=${encodeURIComponent(category.name)}`}
                                className="btn btn-sm btn-outline-primary"
                                target="_blank"
                                title="View Events"
                              >
                                <i className="bi bi-calendar-event"></i>
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleOpenEditModal(category)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  setDeleteModal({
                                    show: true,
                                    categoryId: category.id,
                                    categoryName: category.name,
                                    productCount: category.product_count,
                                  })
                                }
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCategory) && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModals}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content card-surface">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingCategory ? 'Edit Category' : 'Create Category'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModals}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {errors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...errors };
                            delete newErrors.general;
                            setErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="alert alert-success mb-3">
                      <small>
                        <i className="bi bi-info-circle me-1"></i>
                        This category will be assigned to <strong>{activeTabData?.title}</strong> and will filter events on the Events page.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Category Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        disabled={saving}
                        maxLength={255}
                        autoFocus
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.slug ? 'is-invalid' : ''}`}
                        value={formData.slug}
                        onChange={(e) => handleChange('slug', e.target.value)}
                        disabled={saving}
                        placeholder="category-slug"
                      />
                      {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
                      <div className="form-text">URL-friendly identifier (auto-generated from name)</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={saving}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.display_order}
                        onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                        disabled={saving}
                      />
                      <div className="form-text">Lower numbers appear first</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Service</label>
                      <input
                        type="text"
                        className="form-control"
                        value={activeTabData?.title || ''}
                        disabled
                      />
                      <div className="form-text">This category is assigned to {activeTabData?.title}</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Parent Category</label>
                      <select
                        className={`form-select ${errors.parent_category_id ? 'is-invalid' : ''}`}
                        value={formData.parent_category_id}
                        onChange={(e) => handleChange('parent_category_id', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">None (Top Level)</option>
                        {categories
                          .filter((cat) => cat.service_slug === activeServiceSlug && cat.id !== editingCategory?.id)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                      {errors.parent_category_id && <div className="invalid-feedback">{errors.parent_category_id}</div>}
                      <div className="form-text">Optional: Assign a parent category for nested hierarchies</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_active"
                          checked={formData.is_active}
                          onChange={(e) => handleChange('is_active', e.target.checked)}
                          disabled={saving}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                          Category is active
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCloseModals}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-arena" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        editingCategory ? 'Update Category' : 'Create Category'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create Event Modal */}
      {showCreateEventModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseEventModal}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content card-surface">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">Create Event for {activeTabData?.title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseEventModal}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleEventSubmit}>
                  <div className="modal-body">
                    {eventErrors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {eventErrors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...eventErrors };
                            delete newErrors.general;
                            setEventErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="alert alert-success mb-3">
                      <small>
                        <i className="bi bi-info-circle me-1"></i>
                        Select a category and fill in event details. The event will appear on the Events page.
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${eventErrors.category_id ? 'is-invalid' : ''}`}
                        value={eventFormData.category_id}
                        onChange={(e) => handleEventChange('category_id', e.target.value)}
                        disabled={eventSaving}
                        required
                      >
                        <option value="">Select Category</option>
                        {filteredCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      {eventErrors.category_id && <div className="invalid-feedback">{eventErrors.category_id}</div>}
                      {filteredCategories.length === 0 && (
                        <div className="form-text text-warning">
                          No categories available. Please create a category first.
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Event Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${eventErrors.name ? 'is-invalid' : ''}`}
                        value={eventFormData.name}
                        onChange={(e) => handleEventChange('name', e.target.value)}
                        disabled={eventSaving}
                        maxLength={200}
                        autoFocus
                      />
                      {eventErrors.name && <div className="invalid-feedback">{eventErrors.name}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={eventFormData.description}
                        onChange={(e) => handleEventChange('description', e.target.value)}
                        disabled={eventSaving}
                      />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Start Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className={`form-control ${eventErrors.startDate ? 'is-invalid' : ''}`}
                          value={eventFormData.startDate}
                          onChange={(e) => handleEventChange('startDate', e.target.value)}
                          disabled={eventSaving}
                          required
                        />
                        {eventErrors.startDate && <div className="invalid-feedback">{eventErrors.startDate}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className={`form-control ${eventErrors.endDate ? 'is-invalid' : ''}`}
                          value={eventFormData.endDate}
                          onChange={(e) => handleEventChange('endDate', e.target.value)}
                          disabled={eventSaving}
                        />
                        {eventErrors.endDate && <div className="invalid-feedback">{eventErrors.endDate}</div>}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={eventFormData.location}
                        onChange={(e) => handleEventChange('location', e.target.value)}
                        disabled={eventSaving}
                        maxLength={200}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Event Image <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                        {eventImagePreview && (
                          <div className="flex-shrink-0">
                            <img
                              src={eventImagePreview}
                              alt="Event preview"
                              className="rounded"
                              style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                            />
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <input
                            type="file"
                            className={`form-control ${eventErrors.image ? 'is-invalid' : ''}`}
                            accept="image/*"
                            onChange={handleEventFileChange}
                            disabled={eventSaving}
                          />
                          {eventErrors.image && <div className="invalid-feedback">{eventErrors.image}</div>}
                          <div className="form-text">Max 5MB. Formats: JPEG, PNG, GIF, WEBP</div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={eventFormData.status}
                          onChange={(e) => handleEventChange('status', e.target.value)}
                          disabled={eventSaving}
                        >
                          <option value="">Select status</option>
                          <option value="Registration Open">Registration Open</option>
                          <option value="Coming Soon">Coming Soon</option>
                          <option value="Ongoing">Ongoing</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="registrationOpen"
                          checked={eventFormData.registrationOpen}
                          onChange={(e) => handleEventChange('registrationOpen', e.target.checked)}
                          disabled={eventSaving}
                        />
                        <label className="form-check-label" htmlFor="registrationOpen">
                          Registration is open
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCloseEventModal}
                      disabled={eventSaving}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-arena" disabled={eventSaving}>
                      {eventSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        'Create Event'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteCategoryModal
        show={deleteModal.show}
        categoryName={deleteModal.categoryName}
        productCount={deleteModal.productCount}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, categoryId: null, categoryName: '' })}
      />
    </div>
  );
};

export default AdminProgramsPage;
